import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import axios from 'axios';
import { PolicyContextModel, PolicyItem } from '../lib/models/interfaces';
import { UserContext } from './authContext';
import { useAlert } from './alertContext';
import { createLogger } from '../helpers/Logger';

interface Props {
  children?: ReactNode;
}

export const PolicyContext = createContext<PolicyContextModel | undefined>(
  undefined
);

export const usePolicyContext = (): PolicyContextModel => {
  const context = useContext(PolicyContext);

  if (!context) {
    throw new Error(
      'usePolicyContext must be used within a PolicyContextProvider'
    );
  }

  return context;
};

const logger = createLogger({ prefix: '[PolicyContext]' });

export const PolicyContextProvider: React.FC<Props> = ({ children }) => {
  const { auth, token, isExpired } = useContext(UserContext);
  const { handleError } = useAlert();
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [getPoliciesLoading, setGetPoliciesLoading] = useState<boolean>(false);
  const [addPolicyLoading, setAddPolicyLoading] = useState<boolean>(false);
  const [updatePolicyLoading, setUpdatePolicyLoading] =
    useState<boolean>(false);

  const policyUrl = `${process.env.REACT_APP_KORU_API}/policy`;

  const getPolicies = async () => {
    if (!token || isExpired(token)) {
      return;
    }

    setGetPoliciesLoading(true);

    try {
      const response = await axios.post(`${policyUrl}?type=get`, {}, auth);
      const result = response.data?.result;
      const nextPolicies = Array.isArray(result)
        ? result
        : Array.isArray(result?.policies)
          ? result.policies
          : [];

      setPolicies(nextPolicies);
    } catch (e) {
      const error = e as any;
      logger.error('error getting policies', error);
      handleError(error);
    } finally {
      setGetPoliciesLoading(false);
    }
  };

  const addPolicy = async (policy: Partial<PolicyItem>) => {
    if (!token || isExpired(token)) {
      return;
    }

    setAddPolicyLoading(true);

    try {
      const response = await axios.post(
        `${policyUrl}?type=addNew`,
        policy,
        auth
      );
      const result = response.data?.result;

      if (result && typeof result === 'object' && !Array.isArray(result)) {
        const createdPolicy = result as PolicyItem;
        setPolicies((previousPolicies) => {
          if (
            createdPolicy._id &&
            previousPolicies.some((item) => item._id === createdPolicy._id)
          ) {
            return previousPolicies.map((item) =>
              item._id === createdPolicy._id ? createdPolicy : item
            );
          }

          return [createdPolicy, ...previousPolicies];
        });
      } else {
        await getPolicies();
      }

      return result;
    } catch (e) {
      const error = e as any;
      logger.error('error adding policy', error);
      handleError(error);
      return e;
    } finally {
      setAddPolicyLoading(false);
    }
  };

  const updatePolicy = async (
    policyId: string,
    update: Partial<PolicyItem>
  ) => {
    if (!token || isExpired(token)) {
      return;
    }

    setUpdatePolicyLoading(true);

    try {
      const response = await axios.post(
        `${policyUrl}?type=update`,
        { _id: policyId, update },
        auth
      );
      const result = response.data?.result;

      if (result && typeof result === 'object' && !Array.isArray(result)) {
        const updatedPolicy = result as PolicyItem;
        setPolicies((previousPolicies) =>
          previousPolicies.map((item) =>
            item._id === policyId ? { ...item, ...updatedPolicy } : item
          )
        );
      } else {
        setPolicies((previousPolicies) =>
          previousPolicies.map((item) =>
            item._id === policyId ? { ...item, ...update } : item
          )
        );
      }

      return result;
    } catch (e) {
      const error = e as any;
      logger.error('error updating policy', error);
      handleError(error);
      return e;
    } finally {
      setUpdatePolicyLoading(false);
    }
  };

  useEffect(() => {
    getPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, token]);

  return (
    <PolicyContext.Provider
      value={{
        policies,
        setPolicies,
        getPolicies,
        getPoliciesLoading,
        addPolicy,
        addPolicyLoading,
        updatePolicy,
        updatePolicyLoading
      }}
    >
      {children}
    </PolicyContext.Provider>
  );
};
